import { useEffect, useRef, useState } from 'react';

// A tela cheia da galeria de fotos — o "ofusca o fundo" da peça. Nasceu na
// ficha (Peca.tsx) e virou componente quando a busca passou a abrir a foto no
// lugar (pedido do dono, 12/08/2026): um carrossel só, para os dois lugares
// não divergirem em gesto nem em atalho.
//
// Passa para o lado com o dedo (swipe), com as setas na tela e com as setas do
// TECLADO; fecha no botão e no Esc — no balcão é celular, mas na mesa do
// escritório é teclado, e modal que ignora Esc parece travado.
//
// Sem biblioteca: a CSP não deixa script de fora, e isto são 60 linhas.

export interface FotoDaGaleria {
  id: string;
  updated_at: string;
}

export default function GaleriaCheia({ fotos, urlDe, aoFechar }: {
  fotos: FotoDaGaleria[];
  /** Montador da URL — vem de fora para o componente não conhecer consultas. */
  urlDe: (foto: FotoDaGaleria) => string;
  aoFechar: () => void;
}) {
  const [indice, setIndice] = useState(0);
  const toqueX = useRef<number | null>(null);

  const ir = (delta: number) =>
    setIndice((i) => Math.min(fotos.length - 1, Math.max(0, i + delta)));

  // Esc fecha, setas navegam. No documento, e não num onKeyDown do container:
  // o overlay não tem foco de teclado ao abrir, e exigir um clique antes de o
  // Esc funcionar é o tipo de quase-quebrado que ninguém reporta.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
      else if (e.key === 'ArrowLeft') ir(-1);
      else if (e.key === 'ArrowRight') ir(1);
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
    // aoFechar estável (setState do pai); fotos.length não muda com o modal aberto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const foto = fotos[indice];
  if (!foto) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-ink/95 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Fotos da peça"
      onTouchStart={(e) => { toqueX.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        const inicio = toqueX.current;
        toqueX.current = null;
        const fim = e.changedTouches[0]?.clientX;
        if (inicio === null || fim === undefined) return;
        // 40px de arrasto mínimo: menos que isso é toque tremido, não swipe.
        if (fim - inicio > 40) ir(-1);
        else if (inicio - fim > 40) ir(1);
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold">{indice + 1} de {fotos.length}</span>
        <button type="button" className="border border-edge px-3 py-2 text-sm font-bold"
                onClick={aoFechar}>
          Fechar
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center py-3">
        <img src={urlDe(foto)} alt={`Foto ${indice + 1}`}
             className="max-h-full max-w-full object-contain" />
      </div>
      {fotos.length > 1 && (
        <div className="flex justify-center gap-3">
          <button type="button" className="botao-secundario px-6" disabled={indice === 0}
                  onClick={() => ir(-1)} aria-label="Foto anterior">
            ‹
          </button>
          <button type="button" className="botao-secundario px-6"
                  disabled={indice === fotos.length - 1}
                  onClick={() => ir(1)} aria-label="Próxima foto">
            ›
          </button>
        </div>
      )}
    </div>
  );
}
